import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import {
	campaignFormSchema,
	type CampaignFormValues,
	CreateBrokeFlexMessageItemDtoType,
	CreateBrokeFlexMessageItemDtoEligibilityType,
} from '@/modules/messaging/pages/CreateBrokeFlexMessagePage/types'
import type {
	BrokeFlexMessageCampaignModel,
	BrokeFlexMessageItemModel,
} from '@/modules/messaging/types'

interface UseCampaignFormProps {
	mode: 'create' | 'edit'
	initialData?: BrokeFlexMessageCampaignModel
}

export function useCampaignForm({ mode, initialData }: UseCampaignFormProps) {
	const form = useForm<CampaignFormValues>({
		resolver: zodResolver(campaignFormSchema),
		defaultValues: getDefaultValues(mode, initialData),
	})

	const {
		fields: stageFields,
		append: appendStage,
		remove: removeStage,
		move: moveStage,
	} = useFieldArray({
		control: form.control,
		name: 'stages',
	})

	useEffect(() => {
		if (mode === 'edit' && initialData?.brokeNotificationSettings) {
			form.reset(getDefaultValues(mode, initialData))
		}
	}, [initialData, mode, form])

	return {
		form,
		stageFields,
		appendStage,
		removeStage,
		moveStage,
	}
}

function getDefaultValues(
	mode: 'create' | 'edit',
	initialData?: BrokeFlexMessageCampaignModel,
): CampaignFormValues {
	if (mode === 'edit' && initialData?.brokeNotificationSettings) {
		return {
			name: initialData.name || '',
			stages: initialData.brokeNotificationSettings.stages.map((stage) => ({
				minutesAfterTrigger: stage.minutesAfterTrigger,
				message: stage.message,
				maxItemsToShow: stage.maxItemsToShow,
				order: stage.order,
				items: stage.items.map((item: BrokeFlexMessageItemModel) => ({
					type: item.type as unknown as CreateBrokeFlexMessageItemDtoType,
					order: item.order,
					eligibilityType:
						item.eligibilityType as unknown as CreateBrokeFlexMessageItemDtoEligibilityType,
					minValue: item.minValue || undefined,
					displayName: item.displayName,
					icon: item.icon || '🎁',
					description: item.description || undefined,
					message: item.message || undefined,
					imageUrl: item.imageUrl || undefined,
					promotionId: item.promotionId || undefined,
					couponCode: item.couponCode || undefined,
					actionUrl: item.actionUrl || undefined,
					enabled: item.enabled,
				})),
			})),
		}
	}

	return {
		name: '',
		stages: [
			{
				minutesAfterTrigger: 0,
				message: 'คุณยังมีโอกาสรับโบนัสอีกมากมาย!',
				maxItemsToShow: 3,
				order: 0,
				items: [],
			},
		],
	}
}
